import React, { useState, useEffect, useRef, useCallback } from 'react';
import FeaturedEventCard from './FeaturedEventCard';
import { fetchFeaturedEvents, searchEvents, transformPeatixEvent } from '../../utils/peatix-api';
import type { FeaturedEventQuery, EventSearchQuery } from '../../types/peatix-api';
import type { TranslatableEvent } from '../../types/translation';

export interface FeaturedEventsSectionProps {
  /** Optional. When omitted with subtitle, the default header is not rendered (e.g. when using beforeContent). */
  title?: string;
  /** Optional. When omitted with title, the default header is not rendered. */
  subtitle?: string;
  /** Optional. Override section background, e.g. "bg-gray-50" for alternating layout. Default: "bg-white". */
  sectionClassName?: string;
  fetchMode?: 'featured' | 'search';
  featuredQuery?: Partial<FeaturedEventQuery>;
  searchQuery?: Partial<EventSearchQuery>;
  itemsPerPage?: number;
  /** Override loading message. Default: "Loading events..." */
  loadingText?: string;
  /** Override empty state message. Default: "No events found." */
  emptyText?: string;
  /** Rendered before the default header (or before loading/events when no header). Use for a custom hero (e.g. H1 + intro) that replaces title/subtitle. */
  beforeContent?: React.ReactNode;
  /** Rendered after the events grid (and pagination) inside the section. */
  afterContent?: React.ReactNode;
  /** Optional. Override the inner container sizing/padding. */
  containerClassName?: string;
  /** Callback when events are fetched */
  onEventsFetched?: (events: TranslatableEvent[]) => void;
  /** Override events to display (for translation) */
  eventsOverride?: TranslatableEvent[];
}

/** Number of cards visible per "slide" at each breakpoint. */
const CARDS_PER_VIEW_LG = 3;
const CARDS_PER_VIEW_MD = 2;
const CARDS_PER_VIEW_SM = 1;

function useCardsPerView() {
  const [cardsPerView, setCardsPerView] = useState(CARDS_PER_VIEW_LG);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth >= 1024) setCardsPerView(CARDS_PER_VIEW_LG);
      else if (window.innerWidth >= 768) setCardsPerView(CARDS_PER_VIEW_MD);
      else setCardsPerView(CARDS_PER_VIEW_SM);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return cardsPerView;
}

const FeaturedEventsSection: React.FC<FeaturedEventsSectionProps> = ({
  title,
  subtitle,
  sectionClassName,
  fetchMode = 'featured',
  featuredQuery = {},
  searchQuery = {},
  itemsPerPage = 6,
  loadingText = 'Loading events...',
  emptyText = 'No events found.',
  beforeContent,
  afterContent,
  containerClassName,
  onEventsFetched,
  eventsOverride,
}) => {
  const [events, setEvents] = useState<TranslatableEvent[]>([]);
  const displayedEvents: TranslatableEvent[] = eventsOverride || events;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const cardsPerView = useCardsPerView();
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEventsData();
  }, [fetchMode]);

  // Reset carousel position when cardsPerView changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [cardsPerView]);

  const fetchEventsData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let response;

      if (fetchMode === 'featured') {
        response = await fetchFeaturedEvents({
          currentPage: 1,
          itemPerPage: itemsPerPage,
          ...featuredQuery,
        });
      } else {
        response = await searchEvents({
          currentPage: 1,
          itemPerPage: itemsPerPage,
          ...searchQuery,
        });
      }

      if (response.data) {
        const transformedEvents = response.data.map(transformPeatixEvent);
        setEvents(transformedEvents);
        if (onEventsFetched) {
          onEventsFetched(transformedEvents);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch events:', err);

      let errorMessage = 'Failed to load events. Please try again later.';

      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.name === 'TypeError') {
        errorMessage = 'Network error: Unable to connect to the API. Please check your connection and API configuration.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const maxIndex = Math.max(0, displayedEvents.length - cardsPerView);
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < maxIndex;

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - cardsPerView));
  }, [cardsPerView]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + cardsPerView));
  }, [cardsPerView, maxIndex]);

  // Each card takes up 1/cardsPerView of the container width, minus gap adjustments.
  // We use percentage-based translate for responsiveness.
  const GAP_PX = 24; // gap-6 = 24px
  const translateX = currentIndex > 0
    ? `calc(-${(currentIndex / cardsPerView) * 100}% - ${(currentIndex / cardsPerView) * GAP_PX}px)`
    : '0%';

  return (
    <section className={`${sectionClassName || 'bg-white'} py-16`}>
      <div className={containerClassName ?? "container mx-auto px-4 md:px-8 w-full max-w-[1280px]"}>
        {beforeContent}

        {/* Header: only when title or subtitle is provided */}
        {(title || subtitle) && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
            <div className="flex flex-col gap-2">
              {title && <h2 className="text-3xl md:text-4xl font-semibold text-gray-900">{title}</h2>}
              {subtitle && <p className="text-lg text-gray-600">{subtitle}</p>}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="text-gray-600">{loadingText}</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex flex-col justify-center items-center py-12 gap-4">
            <div className="text-red-600 font-semibold">{error}</div>
            <div className="text-sm text-gray-500">
              Check the browser console (F12) for more details.
            </div>
            <button
              onClick={() => fetchEventsData()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Events Carousel */}
        {!isLoading && !error && displayedEvents.length > 0 && (
          <div className="relative">
            {/* Carousel viewport */}
            <div className="overflow-hidden">
              <div
                ref={trackRef}
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(${translateX})`,
                  gap: `${GAP_PX}px`,
                }}
              >
                {displayedEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex-shrink-0"
                    style={{
                      width: `calc((100% - ${(cardsPerView - 1) * GAP_PX}px) / ${cardsPerView})`,
                    }}
                  >
                    <FeaturedEventCard event={event} />
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation arrows */}
            {displayedEvents.length > cardsPerView && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={handlePrev}
                  disabled={!canGoPrev}
                  aria-label="Previous events"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 bg-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  onClick={handleNext}
                  disabled={!canGoNext}
                  aria-label="Next events"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 bg-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && displayedEvents.length === 0 && (
          <div className="flex justify-center items-center py-12">
            <div className="text-gray-600">{emptyText}</div>
          </div>
        )}

        {afterContent}
      </div>
    </section>
  );
};

export default FeaturedEventsSection;
